/* O12 Final Supersavings bundle interaction. Offer 0 keeps the refill toggle;
 * Offers 1–3 make the primary CTA subscription-only, while Offers 2 and 3 add
 * a separate one-time-purchase CTA beneath it. Offer 3 also discloses renewal
 * pricing separately from the introductory price shown on each tile. */
(() => {
  const ROOT_SELECTOR = '[data-o12-bundle]';

  function init(root) {
    if (root.dataset.o12BundleInit) return;
    root.dataset.o12BundleInit = '1';

    const tiles = [...root.querySelectorAll('[data-o12-tile]')];
    const offerMode = root.dataset.offerMode || 'offer_0';
    const priceDisplay = root.dataset.priceDisplay || 'per_unit';
    const subscriptionOnly = ['offer_1', 'offer_2', 'offer_3'].includes(offerMode);
    const variantInput = root.querySelector('[data-o12-variant]');
    const quantityInput = root.querySelector('[data-o12-qty]');
    const planInput = root.querySelector('[data-o12-plan]');
    const refill = root.querySelector('[data-o12-refill]');
    const purchaseButton = root.querySelector('[data-o12-purchase]');
    const oneTimeButton = root.querySelector('[data-o12-purchase-onetime]');
    const purchaseButtons = [purchaseButton, oneTimeButton].filter(Boolean);
    if (!tiles.length || !variantInput || !quantityInput || !planInput || !purchaseButton) return;

    let busy = false;
    let addedTimer = null;

    const selectedTile = () =>
      tiles.find((tile) => tile.getAttribute('aria-checked') === 'true' && !tile.disabled)
      || tiles.find((tile) => !tile.disabled)
      || null;

    const refillIsOn = () => subscriptionOnly
      || Boolean(refill && refill.getAttribute('aria-checked') === 'true');

    function setState(button, state) {
      if (!button) return;
      button.querySelectorAll('[data-o12-purchase-state]').forEach((node) => {
        node.hidden = node.dataset.o12PurchaseState !== state;
      });
    }

    function setBusy(on, activeButton) {
      busy = on;
      purchaseButtons.forEach((button) => {
        if (on) button.setAttribute('aria-disabled', 'true');
        else if (!button.disabled) button.removeAttribute('aria-disabled');
        const buttonSpinner = button.querySelector('[data-o12-purchase-spinner]');
        if (buttonSpinner) buttonSpinner.hidden = !(on && button === activeButton);
      });
    }

    function syncPrices() {
      const useSubscription = refillIsOn();
      tiles.forEach((tile) => {
        const price = tile.querySelector('[data-o12-price]');
        const compare = tile.querySelector('[data-o12-compare]');
        const daily = tile.querySelector('[data-o12-daily]');
        if (!price) return;
        const showTotal = priceDisplay === 'total';
        price.textContent = useSubscription && tile.dataset.plan
          ? (showTotal ? price.dataset.subPriceTotal : price.dataset.subPriceUnit)
          : (showTotal ? price.dataset.onetimePriceTotal : price.dataset.onetimePriceUnit);
        if (daily) {
          const dailyValue = useSubscription && tile.dataset.plan
            ? daily.dataset.subDaily
            : daily.dataset.onetimeDaily;
          daily.textContent = dailyValue ? `${dailyValue} / day` : '';
        }
        if (compare) {
          const compareValue = useSubscription && tile.dataset.plan
            ? (showTotal ? compare.dataset.subCompareTotal : compare.dataset.subCompareUnit)
            : (showTotal ? compare.dataset.onetimeCompareTotal : compare.dataset.onetimeCompareUnit);
          const showCompare = compareValue && compareValue !== price.textContent;
          compare.textContent = showCompare ? compareValue : '';
          compare.hidden = !showCompare;
        }
      });
    }

    function syncInputs(tile) {
      if (!tile) return;
      variantInput.value = tile.dataset.variant || '';
      quantityInput.value = tile.dataset.qty;
      const useSubscription = refillIsOn() && Boolean(tile.dataset.plan);
      planInput.value = useSubscription ? tile.dataset.plan : '';
      planInput.disabled = !useSubscription;
    }

    function syncFrequency(tile) {
      if (!tile) return;
      root.querySelectorAll('[data-o12-frequency]').forEach((node) => {
        node.textContent = tile.dataset.frequency || '';
      });
      root.querySelectorAll('[data-o12-refill-price]').forEach((node) => {
        node.textContent = tile.dataset.refillPrice || '';
      });
    }

    function syncPurchaseAvailability(tile) {
      if (!subscriptionOnly) return;
      const unavailable = !tile || !tile.dataset.plan;
      purchaseButton.disabled = unavailable;
      if (unavailable) purchaseButton.setAttribute('aria-disabled', 'true');
      else if (!busy) purchaseButton.removeAttribute('aria-disabled');
    }

    function setRefill(on) {
      if (!refill) return;
      const tile = selectedTile();
      const nextValue = Boolean(on && tile && tile.dataset.plan);
      refill.setAttribute('aria-checked', nextValue ? 'true' : 'false');
      syncInputs(tile);
      syncPrices();
    }

    function select(tile) {
      if (!tile || tile.disabled) return;
      tiles.forEach((candidate) => {
        const selected = candidate === tile;
        candidate.classList.toggle('is-selected', selected);
        candidate.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
      if (!subscriptionOnly && refillIsOn() && !tile.dataset.plan) setRefill(false);
      else syncInputs(tile);
      syncFrequency(tile);
      syncPurchaseAvailability(tile);
    }

    tiles.forEach((tile) => tile.addEventListener('click', () => select(tile)));
    if (refill) refill.addEventListener('click', () => setRefill(!refillIsOn()));

    const planIds = new Set(
      tiles.filter((tile) => tile.dataset.plan).map((tile) => String(tile.dataset.plan)),
    );

    function showError(errorMessage) {
      const wrapper = root.querySelector('.o12-bundle__purchase-error');
      const message = wrapper && wrapper.querySelector('[data-o12-purchase-error-message]');
      if (wrapper && message) {
        message.textContent = errorMessage || "Sorry — we couldn't update your cart. Please try again.";
        wrapper.removeAttribute('hidden');
      }
    }

    function hideError() {
      const wrapper = root.querySelector('.o12-bundle__purchase-error');
      if (wrapper) wrapper.setAttribute('hidden', '');
    }

    function openCart() {
      if (window.NuoraUpcart && typeof window.NuoraUpcart.open === 'function') {
        window.NuoraUpcart.open();
      }
    }

    async function addItem(variantId, quantity, sellingPlan) {
      const shopRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      const item = { id: Number(variantId), quantity };
      if (sellingPlan) item.selling_plan = sellingPlan;
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      if (!response.ok) throw new Error(`Cart add failed (${response.status})`);
      return response.json();
    }

    async function submit({ oneTime = false, button = purchaseButton } = {}) {
      if (busy) return;
      const tile = selectedTile();
      const variantId = variantInput && variantInput.value;
      const quantity = Math.max(1, parseInt(quantityInput.value, 10) || 1);
      const selectedPlan = !oneTime && !planInput.disabled ? String(planInput.value || '') : '';
      if (!tile || !variantId) return;
      if (subscriptionOnly && !oneTime && (!selectedPlan || !planIds.has(selectedPlan))) {
        showError('A subscription plan is unavailable for this refill interval.');
        return;
      }

      clearTimeout(addedTimer);
      hideError();
      setBusy(true, button);
      setState(button, 'loading');
      try {
        if (selectedPlan && planIds.has(selectedPlan)) {
          await addItem(variantId, quantity, selectedPlan);
        } else {
          await addItem(variantId, quantity);
        }
        openCart();
        setBusy(false, button);
        setState(button, 'added');
        addedTimer = window.setTimeout(() => setState(button, 'idle'), 1500);
      } catch (error) {
        console.error('[o12-bundle] cart add failed', error);
        showError();
        setBusy(false, button);
        setState(button, 'idle');
      }
    }

    purchaseButton.addEventListener('click', () => submit());
    if (oneTimeButton) {
      oneTimeButton.addEventListener('click', () => submit({ oneTime: true, button: oneTimeButton }));
    }

    const initialTile = selectedTile();
    if (initialTile) select(initialTile);
    else syncPurchaseAvailability(null);
    syncPrices();

    const productInfo = root.closest('product-info');
    const sectionId = productInfo && productInfo.dataset.section;
    const variantPicker = productInfo && productInfo.querySelector('variant-selects, variant-radios');
    if (variantPicker) {
      variantPicker.addEventListener('change', () => {
        window.setTimeout(() => {
          if (!variantPicker.currentVariant) return;
          const matchingTile = tiles.find(
            (tile) => String(tile.dataset.variant) === String(variantPicker.currentVariant.id),
          );
          if (matchingTile) select(matchingTile);
        }, 0);
      });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (!root.isConnected || !event || !event.data || !event.data.variant) return;
        if (sectionId && event.data.sectionId !== sectionId) return;
        const matchingTile = tiles.find(
          (tile) => String(tile.dataset.variant) === String(event.data.variant.id),
        );
        if (matchingTile) select(matchingTile);
      });
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();

